import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, Cell } from "recharts";
import { GlassCard, GlassCardContent, GlassCardHeader, GlassCardTitle } from "@/components/ui/glass-card";
import { ChartContainer, ChartTooltipContent } from "@/components/ui/chart";
import { aggregateAppVersions, getAppColor } from "@/utils/appAnalytics";
import { Device } from "@/types/device";
import { Application } from "@/types/application";

interface AppVersionDistributionChartProps {
  devices: Device[];
  applications: Application[];
}

const AppVersionDistributionChart = ({ devices, applications }: AppVersionDistributionChartProps) => {
  const appVersionData = aggregateAppVersions(devices, applications);
  
  // Prepare data for chart - flatten versions for each app
  const chartData = appVersionData.flatMap(app => 
    app.versions.map(version => ({
      appName: app.name,
      version: version.version,
      count: version.count,
      isLatest: version.isLatest,
      displayName: `${app.name} ${version.version}`
    }))
  ).slice(0, 15); // Limit to prevent overcrowding

  const chartConfig = {
    count: {
      label: "Device Count",
      color: "hsl(220 70% 50%)",
    },
  };

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-background border rounded-lg shadow-md p-3">
          <p className="font-medium">{data.displayName}</p>
          <p className="text-sm text-muted-foreground">
            {data.count} devices
            {data.isLatest && <span className="text-green-600 ml-2">(Latest)</span>}
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <GlassCard variant="strong" hover={false}>
      <GlassCardHeader>
        <GlassCardTitle className="flex items-center gap-2">
          App Version Distribution
          <span className="text-sm font-normal text-muted-foreground">
            Top applications across fleet
          </span>
        </GlassCardTitle>
      </GlassCardHeader>
      <GlassCardContent>
        <ChartContainer config={chartConfig} className="h-[400px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={chartData}
              margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
            >
              <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
              <XAxis 
                dataKey="displayName"
                angle={-45}
                textAnchor="end"
                height={80}
                fontSize={12}
                className="fill-muted-foreground"
              />
              <YAxis className="fill-muted-foreground" />
              <Tooltip content={CustomTooltip} />
              <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                {chartData.map((entry, index) => {
                  const baseColor = getAppColor(entry.displayName, index);
                  // Use opacity to distinguish latest vs older versions
                  const opacity = entry.isLatest ? 1 : 0.6;
                  return (
                    <Cell 
                      key={`cell-${index}`} 
                      fill={baseColor}
                      fillOpacity={opacity}
                    />
                  );
                })}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartContainer>
        <div className="flex items-center justify-center gap-6 mt-4 text-sm">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-muted-foreground opacity-100" />
            <span>Latest Version</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-muted-foreground opacity-60" />
            <span>Older Version</span>
          </div>
        </div>
      </GlassCardContent>
    </GlassCard>
  );
};

export default AppVersionDistributionChart;